import { useEffect, useState } from 'react';
import { Button, Col, Modal, Row, Tabs, Tooltip } from 'antd';
import { Asset } from '../ModalAddItemActivo';
import { EyeOutlined } from '@ant-design/icons';
import { CustomButton, CustomTable, FormInput } from '../../../../../components';
import ModalShowEvaluation from '../ModalShowEvaluation';
import { Evaluation } from '../../../types/evaluations-types';

interface IModal {
  isModalAddItem: boolean;
  setIsModalAddItem: (value: boolean) => void;
  formik?: any;
  onAddItem: (item: any) => void;
  selectedItem: Asset | null;
}

const ModalAddItemRolActivo = ({ setIsModalAddItem, isModalAddItem, selectedItem }: IModal) => {
  const [activeTab, setActiveTab] = useState("1");
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isModalShowEvaluation, setIsModalShowEvaluation] = useState(false);
  const [selectedItemForEvaluation, setSelectedItemForEvaluation] = useState<Evaluation | null>(null)

  const hasEvaluations = selectedItem?.evaluations && selectedItem.evaluations.length > 0;
  const evaluationsData = (selectedAsset?.evaluations || []).map((evaluation) => ({
    ...evaluation,
  }));
  const SIN_REGISTRO = 'Sin registro'

  const activeTabs = [
    "1",
    ...(selectedAsset?.vehicle ? ["2"] : []),
    ...(hasEvaluations ? ["3"] : []),
  ];

  const currentTabIndex = activeTabs.indexOf(activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === activeTabs.length - 1;

  useEffect(() => {
    if (selectedItem) {
      setSelectedId(selectedItem.id.toString());
      setSelectedAsset(selectedItem);
    } else {
      setSelectedId(null);
    }
  }, [selectedItem]);

  const columnsAdditions = [
    {
      title: 'Ítem',
      dataIndex: 'item_id',
      key: 'item_id',
      render: (_: any, record: Asset) => record.item?.id || SIN_REGISTRO,
    },
    {
      title: 'Código/placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
      render: (_: any, record: Asset) => record.plate_code || SIN_REGISTRO,
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      render: (_: any, record: Asset) => record.description || SIN_REGISTRO,
    },
    {
      title: 'Clase de activos',
      dataIndex: 'asset_class',
      key: 'asset_class',
      render: (_: any, record: Asset) =>
        record.asset_class
          ? `${record.asset_class.code} - ${record.asset_class.name}`
          : SIN_REGISTRO,
    },
    {
      title: 'Artículo',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (_: any, record: Asset) =>
        record.item
          ? `${record.item.code} - ${record.item.name}`
          : SIN_REGISTRO,
    },
    {
      title: 'Estado del bien',
      dataIndex: 'good_status',
      key: 'good_status',
      render: (_: any, record: Asset) => record.good_status?.status || SIN_REGISTRO,
    },
  ];

  const columnsPredefinedLists = [
    {
      title: 'Código',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Lista predefinida',
      dataIndex: 'predefined_list',
      key: 'predefined_list',
    },
    {
      title: 'Característica o aspecto',
      dataIndex: 'predefined_list_aspect',
      key: 'predefined_list_aspect',
    },
    {
      title: 'Descripción de la revisión',
      dataIndex: 'predefined_list_description',
      key: 'predefined_list_description',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: Evaluation) => (
        <Tooltip title="Ver evaluación">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedItemForEvaluation(record);
              setIsModalShowEvaluation(true);
            }}
          />
        </Tooltip>
      ),
    }
  ];

  return (
    <div>
      <Modal
        width='95vw'      
        title="Ver activo"
        open={isModalAddItem}
        onCancel={() => setIsModalAddItem(false)}
        footer={[
          !isFirstTab && (
            <CustomButton
              key="back"
              label="Atrás"
              onClick={() => setActiveTab(activeTabs[currentTabIndex - 1])}
              buttonType="secondary"
            />
          ),
          !isLastTab && (
            <CustomButton
              key="next"
              label="Continuar"
              onClick={() => setActiveTab(activeTabs[currentTabIndex + 1])}
            />
          ),
          isLastTab && (
            <CustomButton
              key="finish"
              label="Finalizar"
              onClick={() => setIsModalAddItem(false)}
            />
          )
        ]}
      >
        <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
          <Tabs.TabPane tab="Datos del activo" key="1">
            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Código/Placa" name="plate_code" value={selectedAsset?.plate_code || SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Descripción" name="description" value={selectedAsset?.description || SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Número de serie" name="serial_number" value={selectedAsset?.serial_number || SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Vida útil del activo" name="useful_life_asset" value={`${Number(selectedAsset?.remaining_useful_life_days ?? 0) + Number(selectedAsset?.depreciated_useful_life ?? 0)}`} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput
                  label="Grupo de activos"
                  name="asset_group"
                  value={
                    selectedAsset?.asset_group?.code || selectedAsset?.asset_group?.name
                      ? `${selectedAsset.asset_group.code || ''} - ${selectedAsset.asset_group.name || ''}`.trim()
                      : SIN_REGISTRO
                  }
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput
                  label="Clase de activo"
                  name="asset_class"
                  value={
                    selectedAsset?.asset_class?.code || selectedAsset?.asset_class?.name
                      ? `${selectedAsset.asset_class.code || ''} - ${selectedAsset.asset_class.name || ''}`.trim()
                      : SIN_REGISTRO
                  }
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput
                  label="Artículo"
                  name="item"
                  value={
                    selectedAsset?.item?.code || selectedAsset?.item?.name
                      ? `${selectedAsset.item.code || ''} - ${selectedAsset.item.name || ''}`.trim()
                      : SIN_REGISTRO
                  }
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Vida útil depreciada" name="depreciated_useful_life" value={selectedAsset?.depreciated_useful_life ?? 0} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Vida útil por depreciar" name="useful_life_to_be_depreciateds" value={selectedAsset?.remaining_useful_life_days ?? 0} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput
                  label="Responsable"
                  name="responsible"
                  value={
                    selectedAsset?.responsible?.document ||
                      selectedAsset?.responsible?.name ||
                      selectedAsset?.responsible?.last_name
                      ? `${selectedAsset.responsible.document || ''} - ${selectedAsset.responsible.name || ''} ${selectedAsset.responsible.last_name || ''}`.trim()
                      : SIN_REGISTRO
                  }
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput
                  label="Centro de costos"
                  name="cost_center"
                  value={
                    selectedAsset?.cost_center?.code || selectedAsset?.cost_center?.name
                      ? `${selectedAsset.cost_center.code || ''} - ${selectedAsset.cost_center.name || ''}`.trim()
                      : SIN_REGISTRO
                  }
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Estado del bien" name="good_status" value={selectedAsset?.good_status || SIN_REGISTRO} disabled />
              </Col>
            </Row>

            <Row>
              <Col xs={24}>
                <h4>Adiciones</h4>
                <CustomTable
                  columns={columnsAdditions}
                  dataSource={selectedAsset && Array.isArray(selectedAsset.assets)
                    ? selectedAsset.assets
                      .filter(a => a && typeof a === 'object')
                      .map((a, index) => ({ ...a, key: index }))
                    : []}
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 'max-content' }} />
              </Col>
            </Row>
          </Tabs.TabPane>

          {selectedAsset?.vehicle && (
            <Tabs.TabPane tab="Vehículo" key="2">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Placa" name="license_plate" value={selectedAsset.vehicle.license_plate || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Número de motor" name="engine_number" value={selectedAsset.vehicle.engine_number || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Número de chasis" name="chassis_number" value={selectedAsset.vehicle.chassis_number || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Fecha tecnomecánica" name="mechanical_inspection_date" value={selectedAsset.vehicle.mechanical_inspection_date || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Tipo de seguro" name="" value='SOAT' disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Fecha de expedición" name="soat_date" value={selectedAsset.vehicle.soat_date || SIN_REGISTRO} disabled />
                </Col>
                {/* <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Fecha de vencimiento" name="soat_date" value={selectedAsset.vehicle.soat_expiration_date || SIN_REGISTRO} disabled />
                </Col> */}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Número de puertas" name="doors" value={selectedAsset.vehicle.doors || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Nivel de blindaje" name="armor_level" value={selectedAsset.vehicle.armor_level || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Tipo de combustible" name="fuel_type" value={selectedAsset.vehicle.fuel_type || SIN_REGISTRO} disabled />
                </Col>
              </Row>

            </Tabs.TabPane>
          )}

          {hasEvaluations && (
            <Tabs.TabPane tab="Listas predefinidas" key="3">
              <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Estado del elemento" name="status" value={selectedAsset?.status || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Concepto técnico" name="typification" value={selectedAsset?.technical_concept_typification || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Plazo de entrega" name="delivery_deadline" value={selectedAsset?.delivery_deadline || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Rol del experto" name="user_role" value={selectedAsset?.user_role || SIN_REGISTRO} disabled />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput
                    label="Nombre del experto"
                    name="expert"
                    value={
                      selectedAsset?.user_document || selectedAsset?.user
                        ? `${selectedAsset.user_document ?? ''} - ${selectedAsset.user ?? ''}`.trim()
                        : 'sin registro'
                    }
                    disabled
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <FormInput label="Correo" name="email" value={selectedAsset?.user_email || SIN_REGISTRO} disabled />
                </Col>
              </Row>

              <Row>
                <Col xs={24}>
                  <h4>Evaluaciones</h4>
                  <CustomTable
                    columns={columnsPredefinedLists}
                    dataSource={evaluationsData}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 'max-content' }} />
                </Col>
              </Row>
            </Tabs.TabPane>
          )}

        </Tabs>
      </Modal>
      <ModalShowEvaluation
        setIsModalAddItem={setIsModalShowEvaluation}
        isModalAddItem={isModalShowEvaluation}
        selectedItem={selectedItemForEvaluation}
      />
    </div>
  );
};

export default ModalAddItemRolActivo;